import { useMemo, useRef, useState } from 'react'
import { Button } from '../../../shared/ui'
import { deleteSequence, saveSequence } from '../../../lib/outreach/api'
import { BASE_MERGE_FIELDS } from '../../../lib/outreach/merge'
import type { CustomFieldDef, Sequence, SequenceStep } from '../../../lib/outreach/types'
import { getStages } from '../../../lib/stages'
import { toast } from '../../../lib/toast'
import { ConfirmButton, ErrorLine, Field, parseWhole, reportError } from './shared'
import { StepCard } from './StepCard'
import type { StepDraft, StepField } from './StepCard'
import { DEFAULT_SAMPLE, StepPreview } from './StepPreview'
import type { SampleContact } from './StepPreview'

export const MAX_STEPS = 20

interface Props {
  sequence: Sequence | null
  steps: SequenceStep[]
  /** Business phrases plus the phrases of programs that use this sequence. */
  phrases: readonly string[]
  defs: readonly CustomFieldDef[]
  usedBy: number
  readOnly: boolean
  onSaved: () => void
  onDeleted: () => void
  onCancel: () => void
}

type FieldEl = HTMLInputElement | HTMLTextAreaElement
interface Cursor {
  key: number
  field: StepField
  start: number
  end: number
}

let nextKey = 1
function blankStep(dayOffset: string): StepDraft {
  return { key: nextKey++, dayOffset, subject: '', body: '', sameThread: true }
}

function toDrafts(steps: readonly SequenceStep[]): StepDraft[] {
  return steps.map((s) => ({
    key: nextKey++,
    dayOffset: String(s.day_offset),
    subject: s.subject,
    body: s.body,
    sameThread: s.same_thread,
  }))
}

export function SequenceEditor({
  sequence,
  steps: initial,
  phrases,
  defs,
  usedBy,
  readOnly,
  onSaved,
  onDeleted,
  onCancel,
}: Props) {
  const [name, setName] = useState(sequence?.name ?? '')
  const [finalStage, setFinalStage] = useState(sequence?.final_stage ?? '')
  const [steps, setSteps] = useState<StepDraft[]>(() =>
    initial.length > 0 ? toDrafts(initial) : sequence ? [] : [blankStep('0')],
  )
  const [selectedKey, setSelectedKey] = useState<number | null>(steps[0]?.key ?? null)
  const [sample, setSample] = useState<SampleContact>(DEFAULT_SAMPLE)
  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [apiError, setApiError] = useState('')
  const cursor = useRef<Cursor | null>(null)
  const refs = useRef(new Map<string, FieldEl>())

  const customKeys = useMemo(() => defs.map((d) => d.key), [defs])
  const stages = useMemo(() => {
    const list = getStages()
    return finalStage && !list.includes(finalStage) ? [...list, finalStage] : list
  }, [finalStage])
  const tokens = [
    ...BASE_MERGE_FIELDS.map((f) => `{{${f}}}`),
    ...customKeys.map((k) => `{{custom.${k}}}`),
  ]

  const update = (key: number, patch: Partial<StepDraft>) =>
    setSteps((list) => list.map((s) => (s.key === key ? { ...s, ...patch } : s)))

  const move = (i: number, dir: -1 | 1) =>
    setSteps((list) => {
      const j = i + dir
      const a = list[i]
      const b = list[j]
      if (!a || !b) return list
      const next = [...list]
      next[i] = b
      next[j] = a
      return next
    })

  const insertToken = (token: string) => {
    const target =
      cursor.current ?? (selectedKey ? { key: selectedKey, field: 'body' as const } : null)
    const step = target ? steps.find((s) => s.key === target.key) : undefined
    if (!target || !step) return
    const value = step[target.field]
    const start = cursor.current ? cursor.current.start : value.length
    const end = cursor.current ? cursor.current.end : value.length
    const text = value.slice(0, start) + token + value.slice(end)
    update(step.key, target.field === 'subject' ? { subject: text } : { body: text })
    const pos = start + token.length
    cursor.current = { key: step.key, field: target.field, start: pos, end: pos }
    requestAnimationFrame(() => {
      const el = refs.current.get(`${step.key}:${target.field}`)
      if (el) {
        el.focus()
        el.setSelectionRange(pos, pos)
      }
    })
  }

  const stepErrors = steps.map((s) => {
    if (Number.isNaN(parseWhole(s.dayOffset))) return 'Days must be a whole number, 0 or more.'
    if (!s.subject.trim()) return 'Add a subject.'
    if (!s.body.trim()) return 'Add a body.'
    return undefined
  })
  const nameError = !name.trim()
    ? 'Give the sequence a name.'
    : name.trim().length > 120
      ? 'The name is too long (120 characters at most).'
      : undefined
  const invalid = Boolean(nameError) || stepErrors.some(Boolean)

  const save = async () => {
    setSubmitted(true)
    setApiError('')
    if (invalid) return
    setBusy(true)
    try {
      await saveSequence(
        sequence?.id ?? null,
        name.trim(),
        finalStage || null,
        steps.map((s, i) => ({
          day_offset: parseWhole(s.dayOffset),
          subject: s.subject,
          body: s.body,
          same_thread: i === 0 ? false : s.sameThread,
        })),
      )
      toast('Sequence saved', 'success')
      onSaved()
    } catch (e) {
      setApiError(reportError(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!sequence) return
    setBusy(true)
    setApiError('')
    try {
      await deleteSequence(sequence.id)
      toast('Sequence deleted', 'success')
      onDeleted()
    } catch (e) {
      setApiError(reportError(e))
      setBusy(false)
    }
  }

  const selectedIndex = steps.findIndex((s) => s.key === selectedKey)
  const selected = steps[selectedIndex]

  return (
    <div className="os-root" role="group" aria-label={sequence ? 'Edit sequence' : 'New sequence'}>
      <h3 className="os-name">
        {readOnly ? 'Sequence' : sequence ? 'Edit sequence' : 'New sequence'}
      </h3>
      <div className="os-grid">
        <Field id="seq-name" label="Name" error={submitted ? nameError : undefined}>
          <input
            id="seq-name"
            className="settings-input"
            disabled={readOnly}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          id="seq-stage"
          label="Final stage"
          hint="The stage a contact moves to after the last step."
        >
          <select
            id="seq-stage"
            className="form-select"
            disabled={readOnly}
            value={finalStage}
            onChange={(e) => setFinalStage(e.target.value)}
          >
            <option value="">Leave stage unchanged</option>
            {stages.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {!readOnly && (
        <div className="settings-row">
          <span className="settings-label" id="merge-label">
            Insert a merge field at the cursor
          </span>
          <div className="os-chips" role="group" aria-labelledby="merge-label">
            {tokens.map((t) => (
              <Button key={t} variant="secondary" onClick={() => insertToken(t)}>
                {t}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="os-steps">
        {steps.map((s, i) => (
          <StepCard
            key={s.key}
            step={s}
            index={i}
            count={steps.length}
            selected={s.key === selectedKey}
            readOnly={readOnly}
            phrases={phrases}
            customKeys={customKeys}
            error={submitted ? stepErrors[i] : undefined}
            onChange={(patch) => update(s.key, patch)}
            onSelect={() => setSelectedKey(s.key)}
            onTrack={(field, el) => {
              cursor.current = {
                key: s.key,
                field,
                start: el.selectionStart ?? el.value.length,
                end: el.selectionEnd ?? el.value.length,
              }
            }}
            register={(field, el) => {
              if (el) refs.current.set(`${s.key}:${field}`, el)
              else refs.current.delete(`${s.key}:${field}`)
            }}
            onMove={(dir) => move(i, dir)}
            onRemove={() => setSteps((list) => list.filter((x) => x.key !== s.key))}
          />
        ))}
        {steps.length === 0 && <div className="settings-hint">This sequence has no steps.</div>}
      </div>
      {!readOnly && (
        <div className="os-bar">
          <Button
            variant="secondary"
            disabled={steps.length >= MAX_STEPS}
            onClick={() => {
              const s = blankStep('3')
              setSteps((list) => [...list, s])
              setSelectedKey(s.key)
            }}
          >
            Add step
          </Button>
          {steps.length >= MAX_STEPS && (
            <span className="settings-hint">A sequence can have {MAX_STEPS} steps at most.</span>
          )}
        </div>
      )}

      <StepPreview
        stepNo={selected ? selectedIndex + 1 : null}
        subject={selected?.subject ?? ''}
        body={selected?.body ?? ''}
        sample={sample}
        onSample={setSample}
        defs={defs}
      />

      <ErrorLine>{apiError}</ErrorLine>
      <div className="os-bar">
        {!readOnly && (
          <Button disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving...' : 'Save sequence'}
          </Button>
        )}
        <Button variant="secondary" disabled={busy} onClick={onCancel}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && sequence && (
          <span className="os-bar-end">
            <ConfirmButton
              label="Delete sequence"
              confirmLabel="Yes, delete"
              note={
                usedBy > 0
                  ? `${usedBy} program${usedBy === 1 ? '' : 's'} using it will lose their sequence.`
                  : 'No program uses this sequence.'
              }
              disabled={busy}
              onConfirm={() => void remove()}
            />
          </span>
        )}
      </div>
    </div>
  )
}
